import { useMemo, useRef } from 'react'

import { floor50 } from '@/utils'
import { addMilliseconds } from 'date-fns'
import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'

import { nowAtom } from '@/shared/store/clock.store'

import type { Timestamp } from '~shared/utils'
import { CueRunState, fastDeepEqual, GroupRunState } from '~shared/utils'

import { isRunningAtom, runnerLastStopAtom } from '@/store/runner.store'
import { getTimestampByCueIdAtom } from '@/store/timestamps.store'

import type { GroupChildren, GroupTiming } from './group.types'

/**
 * Calculates authoritative group timing from cue timestamps.
 *
 * This hook provides a stable, cached timing object for a group of cues,
 * calculating start/end times and durations for both original (planned)
 * and actual timing data.
 *
 * ## Caching Strategy
 *
 * Uses fastDeepEqual to compare new vs cached timing objects. Only updates
 * the returned reference if values actually changed. This prevents unnecessary
 * re-renders in downstream components.
 *
 * ## Countdown Field
 *
 * The `countdown` field provides live countdown for active groups, respecting
 * pause state:
 *
 * - When running: actualEnd - now (keeps ticking)
 * - When paused: actualEnd - lastStop (frozen at pause time)
 * - null for non-active groups
 *
 * This follows the same pattern as createMoment's pause handling.
 *
 * ## Important Concepts
 *
 * **Duration Performance vs Schedule Timing:**
 *
 * - This measures DURATION PERFORMANCE: "Are we using more/less content time than
 *   planned?"
 * - This does NOT measure schedule timing: "Are we early/late in the schedule?"
 * - A group can run the planned duration but still be late (due to late
 *   start/pause)
 * - A group can finish early but use more duration (because it started late)
 *
 * **Example scenarios:**
 *
 * 1. Future group (not started):
 *
 *    - state: GROUP_FUTURE
 *    - original: { start: 10:00, end: 10:30, duration: 1800000 }
 *    - actual: { start: 10:00, end: 10:30, duration: 1800000 } (matches original)
 * 2. Active group (running on time):
 *
 *    - state: GROUP_ACTIVE
 *    - original: { start: 10:00, end: 10:30, duration: 1800000 }
 *    - actual: { start: 10:00, end: 10:28, duration: 1680000 } (projected to finish
 *      early)
 * 3. Active group (paused in overtime):
 *
 *    - state: GROUP_ACTIVE
 *    - original: { start: 10:00, end: 10:30, duration: 1800000 }
 *    - actual: { start: 10:00, end: 10:32, duration: 1920000 }
 *    - countdown: -120000 (frozen at -2 minutes when paused)
 * 4. Past group (finished, overran):
 *
 *    - state: GROUP_PAST
 *    - original: { start: 10:00, end: 10:30, duration: 1800000 }
 *    - actual: { start: 10:00, end: 10:35, duration: 2100000 } (used more time than
 *      planned)
 *
 * @param groupTimestamps - Array of timestamps for cues in the group (in order)
 *
 * @returns GroupTiming object or null if no timestamps provided
 *
 * @see createTimestamps in ~shared/utils for timestamp structure
 */
export function useGroupTiming(
  groupTimestamps: Timestamp[],
): GroupTiming | null {
  const cacheRef = useRef<GroupTiming | null>(null)
  const isRunning = useAtomValue(isRunningAtom)
  const lastStop = useAtomValue(runnerLastStopAtom)

  const groupActive = groupTimestamps.some(
    (ts) => ts.state === CueRunState.CUE_ACTIVE,
  )

  // selectAtom keeps the value stable at 0 unless the group is active and
  // running, so inactive or paused groups do not re-render on the clock.
  const needsNow = groupActive && isRunning === true
  const nowAtomForGroup = useMemo(
    () => selectAtom(nowAtom, (now) => (needsNow ? now : 0)),
    [needsNow],
  )
  const now = useAtomValue(nowAtomForGroup)

  // Destructure rather than check length: a length check does not narrow the
  // indexed reads below, so they were both possibly undefined.
  const [firstTimestamp] = groupTimestamps
  const lastTimestamp = groupTimestamps.at(-1)

  if (!firstTimestamp || !lastTimestamp) {
    return null
  }

  // Determine group state
  const groupPast = groupTimestamps.every(
    (ts) => ts.state === CueRunState.CUE_PAST,
  )

  const groupState = groupPast
    ? GroupRunState.GROUP_PAST
    : groupActive
      ? GroupRunState.GROUP_ACTIVE
      : GroupRunState.GROUP_FUTURE

  // Calculate times
  const originalStart = firstTimestamp.original.start
  const originalEnd = addMilliseconds(
    lastTimestamp.original.start,
    lastTimestamp.original.duration,
  )
  const originalDuration = floor50(
    originalEnd.getTime() - originalStart.getTime(),
  )
  const actualStart = firstTimestamp.actual.start
  const actualEnd = addMilliseconds(
    lastTimestamp.actual.start,
    lastTimestamp.actual.duration,
  )
  const actualDuration = floor50(actualEnd.getTime() - actualStart.getTime())

  // Calculate countdown for active groups
  let countdown: number | null = null
  if (
    groupState === GroupRunState.GROUP_ACTIVE &&
    isRunning !== undefined &&
    lastStop !== undefined
  ) {
    // Base countdown: time until group projected to finish
    if (isRunning) {
      countdown = actualEnd.getTime() - now
    } else {
      countdown = actualEnd.getTime() - lastStop
    }

    // Adjust countdown if active cue is overtime
    // This accounts for being behind schedule on the current cue
    const activeCue = groupTimestamps.find(
      (ts) => ts.state === CueRunState.CUE_ACTIVE,
    )
    if (activeCue) {
      const activeCueOvertime =
        activeCue.actual.duration - activeCue.original.duration
      if (activeCueOvertime > 0) {
        countdown -= activeCueOvertime
      }
    }

    // Apply floor50 to prevent race conditions in countdown display
    countdown = floor50(countdown)
  }

  // Check if actual differs from original (all 3 fields)
  const actualDiffersFromOriginal =
    actualStart.getTime() !== originalStart.getTime() ||
    actualEnd.getTime() !== originalEnd.getTime() ||
    actualDuration !== originalDuration

  // Build new timing object
  const newTiming: GroupTiming = {
    state: groupState,
    original: {
      start: originalStart,
      end: originalEnd,
      duration: originalDuration,
    },
    actual: {
      start: actualStart,
      end: actualEnd,
      duration: actualDuration,
    },
    countdown,
    actualDiffersFromOriginal,
  }

  // Only update cache if values differ (deep comparison). The ref is read during
  // render on purpose: it keeps the returned object stable across ticks.
  /* oxlint-disable react/refs */
  const cachedTiming = cacheRef.current
  if (!fastDeepEqual(newTiming, cachedTiming)) {
    cacheRef.current = newTiming
  }

  return cacheRef.current
  /* oxlint-enable react/refs */
}

export function useGroupTimestamps(groupCues: GroupChildren): Timestamp[] {
  const getTimestampByCueId = useAtomValue(getTimestampByCueIdAtom)

  return groupCues
    ?.map((cue) => getTimestampByCueId(cue.id))
    .filter(Boolean) as Timestamp[]
}
